import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RecaptchaVerifier, signInWithPhoneNumber, ConfirmationResult } from 'firebase/auth';
import { auth } from '../firebase/config';
import './Auth.css';

declare global {
  interface Window {
    grecaptcha: any;
    recaptchaVerifier?: RecaptchaVerifier;
  }
}

const Signup: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    nmls: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: 'real_estate_agent' as 'loan_originator' | 'real_estate_agent'
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [phoneVerificationStep, setPhoneVerificationStep] = useState<'input' | 'verify' | 'verified'>('input');
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [signupComplete, setSignupComplete] = useState(false);
  const verificationCodeInputRef = useRef<HTMLInputElement>(null);

  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  const functions = getFunctions();

  useEffect(() => {
    if (!window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible',
          callback: () => {},
          'expired-callback': () => {}
        });
        setRecaptchaLoaded(true);
      } catch (error) {
        console.error('Error initializing reCAPTCHA:', error);
      }
    }
    return () => {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = undefined;
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSendVerificationCode = async () => {
    if (!formData.phoneNumber) {
      setError('Please enter a phone number');
      return;
    }
    let phoneNumber = formData.phoneNumber.replace(/\D/g, '');
    if (!phoneNumber.startsWith('1') && phoneNumber.length === 10) {
      phoneNumber = '1' + phoneNumber;
    }
    phoneNumber = '+' + phoneNumber;

    try {
      setSendingCode(true);
      setError('');
      if (!window.recaptchaVerifier) throw new Error('reCAPTCHA not initialized');
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, window.recaptchaVerifier);
      setConfirmationResult(confirmation);
      setPhoneVerificationStep('verify');
      setTimeout(() => verificationCodeInputRef.current?.focus(), 100);
    } catch (err: any) {
      let msg = 'Failed to send verification code';
      if (err.code === 'auth/invalid-phone-number') msg = 'Invalid phone number. Use format: +1 (555) 123-4567';
      else if (err.code === 'auth/too-many-requests') msg = 'Too many attempts. Please try again later.';
      else if (err.message) msg = err.message;
      setError(msg);
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
      }
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    if (!confirmationResult) {
      setError('Please request a new verification code');
      return;
    }
    try {
      setVerifyingCode(true);
      setError('');
      await confirmationResult.confirm(verificationCode);
      setPhoneVerified(true);
      setPhoneVerificationStep('verified');
    } catch {
      setError('Invalid verification code. Please try again.');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.phoneNumber || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }
    if (!phoneVerified) {
      setError('Please verify your phone number first');
      return;
    }
    if (!formData.nmls) {
      setError(formData.userType === 'loan_originator' ? 'NMLS number is required' : 'MLS number is required');
      return;
    }
    if (!acceptedTerms) {
      setError('Please accept the Terms and Conditions');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      setError('');
      setLoading(true);

      if (recaptchaLoaded && window.grecaptcha) {
        const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
        const token = await window.grecaptcha.execute(siteKey, { action: 'signup' });
        const verifyRecaptcha = httpsCallable(functions, 'verifyRecaptcha');
        try {
          await verifyRecaptcha({ token });
        } catch (recaptchaError: any) {
          throw new Error(recaptchaError.message || 'Bot detection failed. Please try again.');
        }
      }

      if (formData.userType === 'loan_originator' && formData.nmls) {
        const verifyNMLS = httpsCallable(functions, 'verifyNMLSID');
        const nmlsResult: any = await verifyNMLS({
          nmlsId: formData.nmls,
          firstName: formData.firstName,
          lastName: formData.lastName,
        });
        if (!nmlsResult.data.verified) {
          setError(nmlsResult.data.message || 'NMLS verification failed');
          setLoading(false);
          return;
        }
      }

      await signup(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName,
        formData.phoneNumber,
        formData.userType,
        formData.nmls || undefined
      );

      setSignupComplete(true);
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  // ── Success Screen ──────────────────────────────────────────────────────────

  if (signupComplete) {
    return (
      <div className="auth-container">
        <div className="auth-card signup-success-card">
          <div className="signup-success-icon">✓</div>
          <h2 className="signup-success-title">Registration Received!</h2>
          <p className="signup-success-desc">
            Thank you, <strong>{formData.firstName}</strong>! Your ScoreMyOffer account registration has been submitted and is pending review.
          </p>

          <div className="signup-success-steps">
            <div className="success-step success-step--done">
              <div className="success-step-icon">✓</div>
              <div className="success-step-body">
                <strong>Account Created</strong>
                <span>Your profile has been saved</span>
              </div>
            </div>
            <div className="success-step success-step--active">
              <div className="success-step-icon">✉</div>
              <div className="success-step-body">
                <strong>Verify Your Email</strong>
                <span>Check <em>{formData.email}</em> for a verification link from Firebase</span>
              </div>
            </div>
            <div className="success-step">
              <div className="success-step-icon">🔍</div>
              <div className="success-step-body">
                <strong>Admin Review</strong>
                <span>Our team will review your credentials and registration</span>
              </div>
            </div>
            <div className="success-step">
              <div className="success-step-icon">🎉</div>
              <div className="success-step-body">
                <strong>Approval Email</strong>
                <span>You'll receive an email at {formData.email} once approved</span>
              </div>
            </div>
          </div>

          <div className="signup-success-note">
            You should have received two emails: a verification link and a registration confirmation.
            If you don't see them, check your spam folder.
          </div>

          <button className="btn-primary" style={{ marginTop: '8px' }} onClick={() => navigate('/login')}>
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  // ── Signup Form ─────────────────────────────────────────────────────────────

  const isAgent = formData.userType === 'real_estate_agent';

  return (
    <div className="auth-container signup-bg">
      <div className="auth-card signup-card">

        {/* ── Left Panel ── */}
        <div className="signup-left-panel">
          <div className="signup-brand">
            <div className="logo-icon">★</div>
            <h1>Score My Offer</h1>
          </div>
          <p className="signup-tagline">
            The professional platform for listing agents to evaluate, score, and compare real estate offers.
          </p>
          <ul className="signup-features">
            <li>
              <span className="signup-feature-icon">📊</span>
              AI-powered PTC Score (0–100)
            </li>
            <li>
              <span className="signup-feature-icon">⚖️</span>
              Side-by-side offer comparison
            </li>
            <li>
              <span className="signup-feature-icon">🏦</span>
              Lender verification &amp; vetting
            </li>
            <li>
              <span className="signup-feature-icon">📄</span>
              Automated PDF data extraction
            </li>
          </ul>
          <div className="signup-already-have">
            Already have an account?{' '}
            <button
              type="button"
              className="link-button"
              onClick={() => navigate(returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login')}
            >
              Sign in
            </button>
          </div>
        </div>

        {/* ── Right Panel (Form) ── */}
        <div className="signup-right-panel">
          <div className="signup-form-header">
            <h2>Create Your Account</h2>
            <p>Fill in the details below to get started</p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="auth-form signup-form">

            {/* Role Selection */}
            <div className="form-section">
              <label className="section-label">I am a <span className="required-asterisk">*</span></label>
              <div className="role-cards">
                <button
                  type="button"
                  className={`role-card ${formData.userType === 'real_estate_agent' ? 'selected' : ''}`}
                  onClick={() => setFormData({ ...formData, userType: 'real_estate_agent', nmls: '' })}
                  disabled={loading}
                >
                  <span className="role-card-icon">🏠</span>
                  <span className="role-card-text">
                    <span className="role-card-title">Real Estate Agent</span>
                    <span className="role-card-desc">Listing or Buyer's Agent</span>
                  </span>
                </button>
                <button
                  type="button"
                  className={`role-card ${formData.userType === 'loan_originator' ? 'selected' : ''}`}
                  onClick={() => setFormData({ ...formData, userType: 'loan_originator', nmls: '' })}
                  disabled={loading}
                >
                  <span className="role-card-icon">🏦</span>
                  <span className="role-card-text">
                    <span className="role-card-title">Loan Originator</span>
                    <span className="role-card-desc">Mortgage Broker / Lender</span>
                  </span>
                </button>
              </div>
            </div>

            {/* Name row */}
            <div className="form-section">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="First name"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last name"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* License + Phone row */}
            <div className="form-section">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nmls">
                    {isAgent ? 'MLS License #' : 'NMLS #'} <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    id="nmls"
                    name="nmls"
                    value={formData.nmls}
                    onChange={handleChange}
                    placeholder={isAgent ? 'MLS membership number' : 'NMLS license number'}
                    disabled={loading}
                  />
                  <span className="field-hint">
                    {isAgent ? 'State real estate license or MLS ID' : 'Will be verified against NMLS registry'}
                  </span>
                </div>
                <div className="form-group">
                  <label htmlFor="phoneNumber">
                    Phone Number <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                    disabled={loading || phoneVerified}
                  />
                  {phoneVerificationStep === 'input' && (
                    <button
                      type="button"
                      className="verify-phone-btn"
                      onClick={handleSendVerificationCode}
                      disabled={sendingCode || !formData.phoneNumber}
                    >
                      {sendingCode ? 'Sending...' : '📱 Send Code'}
                    </button>
                  )}
                  {phoneVerificationStep === 'verify' && (
                    <div className="verification-code-input">
                      <input
                        ref={verificationCodeInputRef}
                        type="text"
                        placeholder="6-digit code"
                        maxLength={6}
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                        disabled={verifyingCode}
                        className="code-input"
                      />
                      <button
                        type="button"
                        className="verify-code-btn"
                        onClick={handleVerifyCode}
                        disabled={verifyingCode || verificationCode.length !== 6}
                      >
                        {verifyingCode ? '...' : 'Verify'}
                      </button>
                      <button
                        type="button"
                        className="resend-code-btn"
                        onClick={handleSendVerificationCode}
                        disabled={sendingCode}
                      >
                        Resend
                      </button>
                    </div>
                  )}
                  {phoneVerificationStep === 'verified' && (
                    <div className="verification-success">✓ Verified</div>
                  )}
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="email">Email Address <span className="required-asterisk">*</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  disabled={loading}
                />
                <span className="field-hint field-hint--important">
                  Use the same email where you receive offer submissions — ScoreMyOffer uses this to match incoming offers to your account.
                </span>
              </div>
            </div>

            {/* Password row */}
            <div className="form-section">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">Password <span className="required-asterisk">*</span></label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Min. 6 characters"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password <span className="required-asterisk">*</span></label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Repeat password"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Terms */}
            <div className="terms-container">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  disabled={loading}
                />
                <span>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="terms-link">Terms and Conditions</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="terms-link">Privacy Policy</a>
                </span>
              </label>
            </div>

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>

            <div id="recaptcha-container"></div>

            <div className="recaptcha-notice">
              Protected by reCAPTCHA —{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
              {' '}·{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms</a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
